import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import expendituresService from '../services/expendituresService';
import s3Service from '../services/s3Service';
import styles from '../assets/expendituresCreate.module.css';
import Loader from '../components/Loader';

const ExpendituresCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [expenditures, setExpenditures] = useState([]);
  const [errors, setErrors] = useState({});
  
  // Available data for dropdowns
  const [expenditureCategories, setExpenditureCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  
  // Typeahead data
  const [properties, setProperties] = useState([]);
  const [propertySearchTerm, setPropertySearchTerm] = useState("");
  const propertyTypeaheadRef = useRef(null);

  const [showDatePicker, setShowDatePicker] = useState({});
  const datePickerRefs = useRef({});

  // Date picker month/year states for navigation
  const [datePickerMonth, setDatePickerMonth] = useState({});
  const [datePickerYear, setDatePickerYear] = useState({});

  // Month names for date picker
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  // Generate year options (current year ± 10 years)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 10; i <= currentYear + 10; i++) {
    years.push(i);
  }

  useEffect(() => {
    loadInitialData();
  }, []);

  // Click outside handler for property typeahead dropdown and date pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (propertyTypeaheadRef.current && !propertyTypeaheadRef.current.contains(event.target)) {
        setProperties([]);
      }
      
      // Handle date picker click outside
      Object.keys(showDatePicker).forEach(index => {
        if (showDatePicker[index] && datePickerRefs.current[index] && !datePickerRefs.current[index].contains(event.target)) {
          setShowDatePicker(prev => ({ ...prev, [index]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch expenditure categories, types, and bank accounts
      const [categoriesResponse, typesResponse, bankAccountsResponse] = await Promise.all([
        expendituresService.getExpenditureCategories(),
        expendituresService.getExpenditureTypes(),
        expendituresService.getBankAccounts()
      ]);

      // Combine categories and types into the expected structure
      if (categoriesResponse && categoriesResponse.success && typesResponse && typesResponse.success) {
        const categories = categoriesResponse.data || [];
        const types = typesResponse.data || [];
        
        // Group types by category
        const categoriesWithTypes = categories.map(category => ({
          category: category.name,
          items: {}
        }));
        
        // Add types to their respective categories
        types.forEach(type => {
          const category = categoriesWithTypes.find(cat => 
            cat.category === categories.find(c => c.id === type.category_id)?.name
          );
          if (category) {
            category.items[type.id] = type.name;
          }
        });
        
        setExpenditureCategories(categoriesWithTypes);
      } else {
        // Mock data for development
        setExpenditureCategories([
          {
            category: "Maintenance",
            items: {
              1: "Plumbing",
              2: "Electrical",
              3: "HVAC",
              4: "General Repairs"
            }
          },
          {
            category: "Utilities",
            items: {
              5: "Electricity",
              6: "Water",
              7: "Gas",
              8: "Internet"
            }
          },
          {
            category: "Insurance",
            items: {
              9: "Property Insurance",
              10: "Liability Insurance"
            }
          }
        ]);
      }

      if (bankAccountsResponse && bankAccountsResponse.success) {
        // Convert array format to associative array format like Laravel
        const bankAccountsObj = {};
        (bankAccountsResponse.data || []).forEach(account => {
          bankAccountsObj[account.id] = account.name;
        });
        setBankAccounts(bankAccountsObj);
      } else {
        // Mock data for development
        setBankAccounts({
          1: "Main Account",
          2: "Secondary Account",
          3: "Emergency Fund"
        });
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      // Mock data for development
      setExpenditureCategories([
        {
          category: "Maintenance",
          items: {
            1: "Plumbing",
            2: "Electrical",
            3: "HVAC",
            4: "General Repairs"
          }
        },
        {
          category: "Utilities",
          items: {
            5: "Electricity",
            6: "Water",
            7: "Gas",
            8: "Internet"
          }
        },
        {
          category: "Insurance",
          items: {
            9: "Property Insurance",
            10: "Liability Insurance"
          }
        }
      ]);
      setBankAccounts({
        1: "Main Account",
        2: "Secondary Account",
        3: "Emergency Fund"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch properties for typeahead
  useEffect(() => {
    const fetchProperties = async () => {
      console.log("🔍 [FRONTEND] Property search term:", propertySearchTerm, "Length:", propertySearchTerm.length);
      if (propertySearchTerm.length >= 1 || propertySearchTerm === "*") {
        console.log("🔍 [FRONTEND] Making API call for property search:", propertySearchTerm);
        try {
          const propertiesData = await expendituresService.searchProperties(propertySearchTerm);
          console.log("🔍 [FRONTEND] API response:", propertiesData);
          if (propertiesData && Array.isArray(propertiesData)) {
            setProperties(propertiesData);
          } else {
            // Mock data for development
            setProperties([
              { 
                id: 1, 
                name: "Property A", 
                property_id: "PA001",
                responseExpenditures: [
                  {
                    category: "Maintenance",
                    items: {
                      1: "Plumbing",
                      2: "Electrical",
                      3: "HVAC",
                      4: "General Repairs"
                    }
                  },
                  {
                    category: "Utilities",
                    items: {
                      5: "Electricity",
                      6: "Water",
                      7: "Gas",
                      8: "Internet"
                    }
                  }
                ],
                bankAccounts: {
                  1: "Main Account",
                  2: "Secondary Account",
                  3: "Emergency Fund"
                }
              },
              { 
                id: 2, 
                name: "Property B", 
                property_id: "PB002",
                responseExpenditures: [
                  {
                    category: "Maintenance",
                    items: {
                      1: "Plumbing",
                      2: "Electrical",
                      3: "HVAC",
                      4: "General Repairs"
                    }
                  }
                ],
                bankAccounts: {
                  1: "Main Account",
                  2: "Secondary Account"
                }
              }
            ]);
          }
        } catch (error) {
          console.error('Error fetching properties:', error);
          // Mock data for development
          setProperties([
            { 
              id: 1, 
              name: "Property A", 
              property_id: "PA001",
              responseExpenditures: [
                {
                  category: "Maintenance",
                  items: {
                    1: "Plumbing",
                    2: "Electrical",
                    3: "HVAC",
                    4: "General Repairs"
                  }
                }
              ],
              bankAccounts: {
                1: "Main Account",
                2: "Secondary Account"
              }
            }
          ]);
        }
      } else {
        setProperties([]);
      }
    };

    const timeoutId = setTimeout(fetchProperties, 300);
    return () => clearTimeout(timeoutId);
  }, [propertySearchTerm]);

  const handlePropertySearch = (e) => {
    const value = e.target.value;
    setPropertySearchTerm(value);
  };

  const handlePropertySelect = (property) => {
    if (!property || !property.id) return;

    const newExpenditure = {
      property_id: property.id,
      property_name: property.name || property.address || "Unknown Property",
      type_id: "",
      date: "",
      creditor: "",
      amount_paid: "",
      invoice_number: "",
      bank_account_id: "",
      purchase_order_number: "",
      description: "",
      file: null,
      confirmation_file: null
    };

    setExpenditures(prev => [...prev, newExpenditure]);
    
    // Update expenditure categories and bank accounts with the selected property's data
    if (property.responseExpenditures) {
      setExpenditureCategories(property.responseExpenditures);
    }
    if (property.bankAccounts) {
      setBankAccounts(property.bankAccounts);
    }
    
    setPropertySearchTerm("");
    setProperties([]);
  };

  const handleExpenditureChange = (index, field, value) => {
    setExpenditures(prev => 
      prev.map((expenditure, i) => 
        i === index ? { ...expenditure, [field]: value } : expenditure
      )
    );
    
    // Clear error when user starts typing
    if (errors[`expenditures.${index}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`expenditures.${index}.${field}`]: ''
      }));
    }
  };

  const handleFileChange = async (index, field, file) => {
    if (!file) return;

    try {
      // Upload file to S3
      // Laravel getDirectoryShort() returns: 'public/expenditures/files/'
      // Actual S3 bucket structure: public/expenditures/files/{filename}
      const fileKey = await s3Service.uploadDocument(file, {
        folder: "public/expenditures/files", // Match Laravel S3 bucket structure
        onProgress: (progress) => {
          console.log(`${field} upload progress:`, progress);
        },
        useLaravelStructure: true, // Use Laravel path structure (no timestamp prefix)
      });

      // Update expenditure with file key
      setExpenditures(prev => 
        prev.map((expenditure, i) => 
          i === index ? { 
            ...expenditure, 
            [field]: file,
            [`${field}Key`]: fileKey 
          } : expenditure
        )
      );

      toast.success(`${field === 'confirmation_file' ? 'Payment confirmation' : 'Invoice'} uploaded successfully!`);
    } catch (error) {
      console.error(`Error uploading ${field}:`, error);
      toast.error(`Failed to upload ${field}`);
    }
  };

  const handleDateClick = (index) => {
    // Initialize month/year if not set
    if (!datePickerMonth[index]) {
      setDatePickerMonth(prev => ({ ...prev, [index]: new Date().getMonth() + 1 }));
    }
    if (!datePickerYear[index]) {
      setDatePickerYear(prev => ({ ...prev, [index]: new Date().getFullYear() }));
    }
    setShowDatePicker(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const selectDate = (index, day, month, year) => {
    const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    handleExpenditureChange(index, 'date', formattedDate);
    setShowDatePicker(prev => ({ ...prev, [index]: false }));
  };

  // Navigation handlers for date picker
  const handlePrevMonth = (index) => {
    const month = datePickerMonth[index] || new Date().getMonth() + 1;
    const year = datePickerYear[index] || new Date().getFullYear();
    if (month === 1) {
      setDatePickerMonth(prev => ({ ...prev, [index]: 12 }));
      setDatePickerYear(prev => ({ ...prev, [index]: year - 1 }));
    } else {
      setDatePickerMonth(prev => ({ ...prev, [index]: month - 1 }));
    }
  };

  const handleNextMonth = (index) => {
    const month = datePickerMonth[index] || new Date().getMonth() + 1;
    const year = datePickerYear[index] || new Date().getFullYear();
    if (month === 12) {
      setDatePickerMonth(prev => ({ ...prev, [index]: 1 }));
      setDatePickerYear(prev => ({ ...prev, [index]: year + 1 }));
    } else {
      setDatePickerMonth(prev => ({ ...prev, [index]: month + 1 }));
    }
  };

  const handleMonthChange = (index, month) => {
    setDatePickerMonth(prev => ({ ...prev, [index]: parseInt(month) }));
  };

  const handleYearChange = (index, year) => {
    setDatePickerYear(prev => ({ ...prev, [index]: parseInt(year) }));
  };

  const generateCalendarDays = (index) => {
    const month = datePickerMonth[index] || new Date().getMonth() + 1;
    const year = datePickerYear[index] || new Date().getFullYear();

    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();

    const days = [];

    // Add empty slots for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return {
      days,
      monthName: monthNames[month - 1],
      year: year,
      month: month
    };
  };

  const removeExpenditure = (index) => {
    setExpenditures(prev => prev.filter((_, i) => i !== index));
  };

  const handleDuplicateExpenditure = (index) => {
    const expenditureToDuplicate = expenditures[index];
    const newExpenditure = {
      ...expenditureToDuplicate,
      id: Date.now(), // Assign a unique ID for React key
      property_id: "", // Clear property_id for new entry
      property_name: "", // Clear property_name for new entry
      type_id: "", // Clear type_id for new entry
      date: "", // Clear date for new entry
      creditor: "", // Clear creditor for new entry
      amount_paid: "", // Clear amount_paid for new entry
      invoice_number: "", // Clear invoice_number for new entry
      bank_account_id: "", // Clear bank_account_id for new entry
      purchase_order_number: "", // Clear purchase_order_number for new entry
      description: "", // Clear description for new entry
      file: null, // Clear file for new entry
      confirmation_file: null // Clear confirmation_file for new entry
    };
    setExpenditures(prev => [...prev, newExpenditure]);
  };

  const validateForm = () => {
    const newErrors = {};

    if (expenditures.length === 0) {
      newErrors.expenditures = "At least one expenditure is required";
    } else {
      expenditures.forEach((expenditure, index) => {
        if (!expenditure.property_id) {
          newErrors[`expenditures.${index}.property_id`] = "Property is required";
        }
        if (!expenditure.type_id) {
          newErrors[`expenditures.${index}.type_id`] = "Expenditure type is required";
        }
        if (!expenditure.date) {
          newErrors[`expenditures.${index}.date`] = "Date is required";
        }
        if (!expenditure.creditor) {
          newErrors[`expenditures.${index}.creditor`] = "Creditor is required";
        }
        if (!expenditure.amount_paid) {
          newErrors[`expenditures.${index}.amount_paid`] = "Amount is required";
        }
        if (!expenditure.invoice_number) {
          newErrors[`expenditures.${index}.invoice_number`] = "Invoice number is required";
        }
        if (!expenditure.bank_account_id) {
          newErrors[`expenditures.${index}.bank_account_id`] = "Bank account is required";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      expenditures.forEach((expenditure, index) => {
        formData.append(`expenses[${index}][property_id]`, expenditure.property_id);
        formData.append(`expenses[${index}][property_name]`, expenditure.property_name);
        formData.append(`expenses[${index}][type_id]`, expenditure.type_id);
        // Laravel expects date in d/m/Y format, which is already formatted correctly
        formData.append(`expenses[${index}][date]`, expenditure.date);
        formData.append(`expenses[${index}][due_date]`, expenditure.date); // Also send as due_date for compatibility
        formData.append(`expenses[${index}][amount_paid]`, expenditure.amount_paid);
        formData.append(`expenses[${index}][creditor]`, expenditure.creditor);
        formData.append(`expenses[${index}][amount]`, expenditure.amount_paid);
        formData.append(`expenses[${index}][invoice_number]`, expenditure.invoice_number);
        formData.append(`expenses[${index}][bank_account_id]`, expenditure.bank_account_id);
        formData.append(`expenses[${index}][purchase_order_number]`, expenditure.purchase_order_number || '');
        formData.append(`expenses[${index}][description]`, expenditure.description || '');
        
        // Add S3 file keys if available
        if (expenditure.fileKey) {
          formData.append(`expenses[${index}][fileKey]`, expenditure.fileKey);
        }
        if (expenditure.confirmationFileKey) {
          formData.append(`expenses[${index}][confirmationFileKey]`, expenditure.confirmationFileKey);
        }
      });

      const response = await expendituresService.createExpenditure(formData);

      if (response && response.success) {
        toast.success(response.message || "Expenditures have been added!");
        setTimeout(() => {
          navigate('/expenditures');
        }, 2000);
      } else {
        toast.error(response?.message || "Failed to create expenditures");
      }
    } catch (error) {
      console.error('Error creating expenditures:', error);
      toast.error("Failed to create expenditures");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbWrapper}>
        <div className={styles.breadcrumb}>
          <span>Expenditures</span> / <span>Create</span>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
            <div className={styles.panelHeading}>
              <div className={styles.panelTitle}>
                <h3>Create Expenditure</h3>
              </div>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.expendituresForm}>
                <fieldset className={styles.formHorizontal}>
                  {/* Expenditures Section */}
                  <div className={styles.reportSectionHeader}>Expenditures</div>
                  
                  <div id="js-add-multiple-expenditures"></div>

                  {/* Property Search */}
                  <div className={styles.row} style={{marginBottom: '15px'}}>
                    <div className={styles.colXs5}>
                      <div className={styles.propertySearchContainer} ref={propertyTypeaheadRef}>
                        <input
                          id="new_field_input"
                          className={`${styles.formControl} ${styles.typeahead} ${styles.jsPropertyFieldTypeahead}`}
                          type="text"
                          placeholder="Type property ID or name (or *)"
                          value={propertySearchTerm}
                          onChange={handlePropertySearch}
                          style={{border: '1px solid #0a7280'}}
                        />
                        {properties.length > 0 && (
                          <div className={styles.typeaheadDropdown}>
                            {properties.map((property) => (
                              <div
                                key={property.id}
                                className={styles.typeaheadItem}
                                onClick={() => handlePropertySelect(property)}
                              >
                                {property.name} ({property.property_id || property.id})
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expenditures List */}
                  <div className={styles.controls}>
                    {expenditures.map((expenditure, index) => (
                      <div key={index} className={styles.mainItem}>
                        <label className={styles.mainItemLabel}>
                          Property: {expenditure.property_name}
                        </label>

                        <div className={styles.mainItemContent}>
                          <input
                            name={`expenditures[${index}][property_id]`}
                            type="hidden"
                            value={expenditure.property_id}
                          />
                          <input
                            name={`expenditures[${index}][property_name]`}
                            type="hidden"
                            value={expenditure.property_name}
                          />

                          <div className={styles.expenditureRow}>
                            <div className={styles.expenditureColSm1}>
                              <button
                                type="button"
                                className={styles.btnRemove}
                                onClick={() => removeExpenditure(index)}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: 'red',
                                  fontWeight: 'bold'
                                }}
                              >
                                Delete
                              </button>
                            </div>

                            <div className={styles.expenditureColSm4}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={`${styles.expenditureControlLabel} ${styles.labelRequired}`}>
                                  Expenditure
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <select
                                    id={`expenditures[${index}][type_id]`}
                                    name={`expenditures[${index}][type_id]`}
                                    className={styles.formControl}
                                    value={expenditure.type_id}
                                    onChange={(e) => handleExpenditureChange(index, 'type_id', e.target.value)}
                                    required
                                    style={{fontSize: '12px'}}
                                  >
                                    <option value="">Please select</option>
                                    {expenditureCategories.map((category, catIndex) => (
                                      <React.Fragment key={catIndex}>
                                        <option disabled>{category.category}</option>
                                        {Object.entries(category.items || {}).map(([id, name]) => (
                                          <option key={id} value={id}>
                                            {name}
                                          </option>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </select>
                                  {errors[`expenditures.${index}.type_id`] && (
                                    <div className={styles.textDanger}>{errors[`expenditures.${index}.type_id`]}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className={styles.expenditureColSm3}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={`${styles.expenditureControlLabel} ${styles.labelRequired}`}>
                                  Date
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <div className={styles.datePickerContainer} ref={el => datePickerRefs.current[index] = el}>
                                    <input
                                      className={`${styles.formControl} ${styles.jsDatepicker}`}
                                      name={`expenditures[${index}][date]`}
                                      value={expenditure.date}
                                      onChange={(e) => handleExpenditureChange(index, 'date', e.target.value)}
                                      onClick={() => handleDateClick(index)}
                                      type="text"
                                      placeholder="dd/mm/yyyy"
                                      readOnly
                                      id={`expenditures[${index}][date]`}
                                    />
                                    {showDatePicker[index] && (
                                      <div className={styles.datePickerDropdown}>
                                        <div className={styles.datePickerHeader}>
                                          <button
                                            type="button"
                                            className={styles.datePickerNavButton}
                                            onClick={() => handlePrevMonth(index)}
                                            aria-label="Previous month"
                                          >
                                            ‹
                                          </button>
                                          <div className={styles.datePickerMonthYear}>
                                            <select
                                              className={styles.datePickerSelect}
                                              value={datePickerMonth[index] || new Date().getMonth() + 1}
                                              onChange={(e) => handleMonthChange(index, e.target.value)}
                                            >
                                              {monthNames.map((month, i) => (
                                                <option key={i} value={i + 1}>
                                                  {month}
                                                </option>
                                              ))}
                                            </select>
                                            <select
                                              className={styles.datePickerSelect}
                                              value={datePickerYear[index] || new Date().getFullYear()}
                                              onChange={(e) => handleYearChange(index, e.target.value)}
                                            >
                                              {years.map((year) => (
                                                <option key={year} value={year}>
                                                  {year}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          <button
                                            type="button"
                                            className={styles.datePickerNavButton}
                                            onClick={() => handleNextMonth(index)}
                                            aria-label="Next month"
                                          >
                                            ›
                                          </button>
                                        </div>
                                        <div className={styles.datePickerGrid}>
                                          <div className={styles.datePickerWeekdays}>
                                            <span>Sun</span>
                                            <span>Mon</span>
                                            <span>Tue</span>
                                            <span>Wed</span>
                                            <span>Thu</span>
                                            <span>Fri</span>
                                            <span>Sat</span>
                                          </div>
                                          <div className={styles.datePickerDays}>
                                            {generateCalendarDays(index).days.map((day, dayIndex) => (
                                              <span
                                                key={dayIndex}
                                                className={`${styles.datePickerDay} ${day ? styles.available : styles.empty}`}
                                                onClick={() => day && selectDate(index, day, generateCalendarDays(index).month, generateCalendarDays(index).year)}
                                              >
                                                {day}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {errors[`expenditures.${index}.date`] && (
                                    <div className={styles.textDanger}>{errors[`expenditures.${index}.date`]}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className={styles.expenditureRow}>
                            <div className={styles.expenditureColSm1}>
                              <button
                                type="button"
                                className={styles.btnDuplicate}
                                onClick={() => handleDuplicateExpenditure(index)}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: 'blue',
                                  fontWeight: 'bold'
                                }}
                              >
                                Duplicate
                              </button>
                            </div>

                            <div className={styles.expenditureColSm4}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={`${styles.expenditureControlLabel} ${styles.labelRequired}`}>
                                  Creditor
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <input
                                    className={styles.formControl}
                                    required
                                    name={`expenditures[${index}][creditor]`}
                                    value={expenditure.creditor}
                                    onChange={(e) => handleExpenditureChange(index, 'creditor', e.target.value)}
                                    type="text"
                                    id={`expenditures[${index}][creditor]`}
                                  />
                                  {errors[`expenditures.${index}.creditor`] && (
                                    <div className={styles.textDanger}>{errors[`expenditures.${index}.creditor`]}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className={styles.expenditureColSm3}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={`${styles.expenditureControlLabel} ${styles.labelRequired}`}>
                                  Amount Paid
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <input
                                    className={styles.formControl}
                                    required
                                    name={`expenditures[${index}][amount_paid]`}
                                    value={expenditure.amount_paid}
                                    onChange={(e) => handleExpenditureChange(index, 'amount_paid', e.target.value)}
                                    type="text"
                                    id={`expenditures[${index}][amount_paid]`}
                                  />
                                  {errors[`expenditures.${index}.amount_paid`] && (
                                    <div className={styles.textDanger}>{errors[`expenditures.${index}.amount_paid`]}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className={styles.expenditureColSm4}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={`${styles.expenditureControlLabel} ${styles.labelRequired}`}>
                                  Invoice number
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <input
                                    className={styles.formControl}
                                    required
                                    name={`expenditures[${index}][invoice_number]`}
                                    value={expenditure.invoice_number}
                                    onChange={(e) => handleExpenditureChange(index, 'invoice_number', e.target.value)}
                                    type="text"
                                    id={`expenditures[${index}][invoice_number]`}
                                  />
                                  {errors[`expenditures.${index}.invoice_number`] && (
                                    <div className={styles.textDanger}>{errors[`expenditures.${index}.invoice_number`]}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className={styles.expenditureRow}>
                            <div className={styles.expenditureColSm1}></div>

                            <div className={styles.expenditureColSm4}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={styles.expenditureControlLabel}>
                                  GG Bank Account
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <select
                                    className={styles.formControl}
                                    required
                                    name={`expenditures[${index}][bank_account_id]`}
                                    value={expenditure.bank_account_id}
                                    onChange={(e) => handleExpenditureChange(index, 'bank_account_id', e.target.value)}
                                    id={`expenditures[${index}][bank_account_id]`}
                                  >
                                    <option value="">Please select</option>
                                    {Object.entries(bankAccounts).map(([id, name]) => (
                                      <option key={id} value={id}>
                                        {name}
                                      </option>
                                    ))}
                                  </select>
                                  {errors[`expenditures.${index}.bank_account_id`] && (
                                    <div className={styles.textDanger}>{errors[`expenditures.${index}.bank_account_id`]}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className={styles.expenditureColSm7}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={styles.expenditureControlLabel}>
                                  Purchase order number
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <input
                                    className={styles.formControl}
                                    name={`expenditures[${index}][purchase_order_number]`}
                                    value={expenditure.purchase_order_number}
                                    onChange={(e) => handleExpenditureChange(index, 'purchase_order_number', e.target.value)}
                                    type="text"
                                    id={`expenditures[${index}][purchase_order_number]`}
                                  />
                                  {errors[`expenditures.${index}.purchase_order_number`] && (
                                    <div className={styles.textDanger}>{errors[`expenditures.${index}.purchase_order_number`]}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className={styles.expenditureColSm12}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={styles.expenditureControlLabel}>
                                  Description
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <textarea
                                    className={styles.formControl}
                                    name={`expenditures[${index}][description]`}
                                    value={expenditure.description}
                                    onChange={(e) => handleExpenditureChange(index, 'description', e.target.value)}
                                    id={`expenditures[${index}][description]`}
                                    rows="3"
                                  />
                                  {errors[`expenditures.${index}.description`] && (
                                    <div className={styles.textDanger}>{errors[`expenditures.${index}.description`]}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className={styles.expenditureRow}>
                            <div className={styles.expenditureColSm1}></div>
                            <div className={styles.expenditureColSm12}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={styles.expenditureControlLabel}>
                                  Invoice
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <div className={styles.inputImage}>
                                    <input
                                      className={styles.formControl}
                                      style={{height: 'auto'}}
                                      name={`expenditures[${index}][file]`}
                                      type="file"
                                      id={`expenditures[${index}][file]`}
                                      onChange={(e) => handleFileChange(index, 'file', e.target.files[0])}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className={styles.expenditureRow}>
                            <div className={styles.expenditureColSm1}></div>
                            <div className={styles.expenditureColSm12}>
                              <div className={styles.expenditureFormGroup}>
                                <label className={styles.expenditureControlLabel}>
                                  Confirmation File
                                </label>
                                <div className={styles.expenditureFormControl}>
                                  <div className={styles.inputImage}>
                                    <input
                                      className={styles.formControl}
                                      style={{height: 'auto'}}
                                      name={`expenditures[${index}][confirmation_file]`}
                                      type="file"
                                      id={`expenditures[${index}][confirmation_file]`}
                                      onChange={(e) => handleFileChange(index, 'confirmation_file', e.target.files[0])}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {errors.expenditures && (
                    <div className={styles.textDanger}>{errors.expenditures}</div>
                  )}
                </fieldset>

                <fieldset className={styles.formHorizontal}>
                  <div className={`${styles.formGroup} ${styles.textRight}`}>
                    <div className={styles.colSm12}>
                      <button
                        type="submit"
                        className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                        disabled={submitting}
                        style={{
                          backgroundColor: "#1B2046",
                          color: "#fff",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "4px",
                          fontSize: "14px",
                          fontWeight: "600"
                        }}
                      >
                        {submitting ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpendituresCreate; 